// Form.tsx
import React, { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { FileUpload } from '../../../../../common/components/fileUpload/FileUpload'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'
import { ItemAttribute, SaleItemInterface } from '../../../../../common/types/interfaces'
import classes from './Form.module.sass'
import { useForm } from './hooks/useForm'
import { SearchSelect } from '../../../../../common/components/searchSelect/SearchSelect'
import { Toggle } from '../../../../../common/components/toggle/Toggle'
import { ItemAttributeForm } from '../../../../../common/components/itemAttributeForm/ItemAttributeForm'
import { ClusterTypes, SaleItemFormView, Unit } from '../../../../../common/types/enums'
import { createTypeSelectList } from '../../../../../common/utils/functions/createTypeSelectList'
import { unitTypeToText } from '../../../../../common/utils/functions/enumToText'
import { RelationList } from './RelationList'
import { StoreToggleList } from '../storeToggleList/StoreToggleList'
import { useSelector } from 'react-redux'
import { selectCurrentCluster } from '../../../../../store/slices/currentClusterSlice'
import { KitList } from './kitList.tsx/KitList'

type Props = {
  edit: boolean
  el?: SaleItemInterface
  data: SaleItemInterface
  setData: React.Dispatch<SetStateAction<SaleItemInterface>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
}

export const Form = ({ edit, data, setData, getList, setModalActive, el }: Props) => {
  const {
    files,
    pairs,
    setFiles,
    setPairs,
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    createCategorySelectList,
    disabled,
    toggleAction,
    view,
    setView,
    selectAttrs,
    attrsDisabled,
    setAttrsDisabled,
    createSaleItemSelectList,
    createAddonSelectList,
    createRecommendationSelectList,
    createKitSelectList,
    saleItemList,
    resetField,
    getSaleItem,
    getItemDisplayText,
    handleAddRelation,
    handleRemoveRelation,
    handleAddKitItem,
    handleRemoveKitItem,
    handleKitQuantityChange,
    storeList,
    handleStopToggle,
    isComplexCategory,
  } = useForm(data, setData, edit, getList, setModalActive, el)

  const currentCluster = useSelector(selectCurrentCluster)

  const showAvailabilityTab = data.isActive && !data.useStock && !data.isAddon

  return (
    <div className={classes.container}>
      <div className={classes.bookMarks}>
        <div
          className={view === SaleItemFormView.FORM ? classes.titleActive : classes.title}
          onClick={() => setView(SaleItemFormView.FORM)}
        >
          {data.id ? 'редактирование' : 'добавление'}
        </div>
        {selectAttrs(Number(data.categoryId))?.length > 0 && !data.isAddon && (
          <div
            className={view === SaleItemFormView.ATTRIBUTE ? classes.titleActive : classes.title}
            onClick={() => setView(SaleItemFormView.ATTRIBUTE)}
          >
            {'параметры'}
          </div>
        )}
        {data.categoryId && !data.isAddon && !isComplexCategory && (
          <div
            className={view === SaleItemFormView.GROUP ? classes.titleActive : classes.title}
            onClick={() => setView(SaleItemFormView.GROUP)}
          >
            {'группа'}
          </div>
        )}
        {!data.isAddon && (
          <div
            className={view === SaleItemFormView.ADDONS ? classes.titleActive : classes.title}
            onClick={() => setView(SaleItemFormView.ADDONS)}
          >
            {'добавки'}
          </div>
        )}
        {!data.isAddon && (
          <div
            className={
              view === SaleItemFormView.RECOMMENDATIONS ? classes.titleActive : classes.title
            }
            onClick={() => setView(SaleItemFormView.RECOMMENDATIONS)}
          >
            {'рекомендации'}
          </div>
        )}
        {!data.isAddon && isComplexCategory && (
          <div
            className={view === SaleItemFormView.KIT ? classes.titleActive : classes.title}
            onClick={() => setView(SaleItemFormView.KIT)}
          >
            {'комплект'}
          </div>
        )}
        {showAvailabilityTab && (
          <div
            className={view === SaleItemFormView.AVAILABILITY ? classes.titleActive : classes.title}
            onClick={() => setView(SaleItemFormView.AVAILABILITY)}
          >
            {'доступность'}
          </div>
        )}
      </div>

      {view === SaleItemFormView.ADDONS && !data.isAddon && (
        <div className={classes.groupFormContainer}>
          <SearchSelect
            onBlur={setDirty}
            fieldName="saleItemId"
            options={createAddonSelectList()}
            value={data.saleItemId}
            onChangeHandler={(value) => handleAddRelation('addons', Number(value))}
            type="text"
            placeholder={
              createAddonSelectList().length
                ? 'Начните ввод и выберите добавку из списка'
                : 'Нет доступных добавок'
            }
            itemValidationResult={getValidationResult('saleItemId')}
          />
          <RelationList
            items={data.addons}
            saleItemList={saleItemList}
            onRemove={(relationId) => handleRemoveRelation('addons', relationId)}
            getItemDisplayText={getItemDisplayText}
            title="Добавленные товары"
          />
        </div>
      )}

      {view === SaleItemFormView.RECOMMENDATIONS && !data.isAddon && (
        <div className={classes.groupFormContainer}>
          <SearchSelect
            onBlur={setDirty}
            fieldName="saleItemId"
            options={createRecommendationSelectList()}
            value={data.saleItemId}
            onChangeHandler={(value) => handleAddRelation('recommendations', Number(value))}
            type="text"
            placeholder={
              createRecommendationSelectList().length
                ? 'Начните ввод и выберите товар из списка'
                : 'Нет доступных товаров'
            }
            itemValidationResult={getValidationResult('saleItemId')}
          />
          <RelationList
            items={data.recommendations}
            saleItemList={saleItemList}
            onRemove={(relationId) => handleRemoveRelation('recommendations', relationId)}
            getItemDisplayText={getItemDisplayText}
            title="Рекомендуемые товары"
          />
        </div>
      )}

      {view === SaleItemFormView.KIT && !data.isAddon && isComplexCategory && (
        <div className={classes.groupFormContainer}>
          <SearchSelect
            onBlur={setDirty}
            fieldName="kitItemId"
            options={createKitSelectList()}
            value={data.kitItemId}
            onChangeHandler={(value) => handleAddKitItem(Number(value))}
            type="text"
            placeholder={
              createKitSelectList().length
                ? 'Начните ввод и выберите товар для комплекта'
                : 'Нет доступных товаров'
            }
            itemValidationResult={getValidationResult('kitItemId')}
          />
          <KitList
            items={data.items}
            saleItemList={saleItemList}
            onRemove={handleRemoveKitItem}
            onQuantityChange={handleKitQuantityChange}
            getItemDisplayText={getItemDisplayText}
            title="Состав комплекта"
          />
        </div>
      )}

      {view === SaleItemFormView.AVAILABILITY && showAvailabilityTab && (
        <div className={classes.groupFormContainer}>
          <StoreToggleList
            stores={storeList}
            item_stops={data.item_stops || []}
            onToggle={handleStopToggle}
            disabled={!data.isActive}
          />
        </div>
      )}

      {view === SaleItemFormView.GROUP && data.categoryId && !data.isAddon && (
        <div className={classes.groupFormContainer}>
          {!data.groupId && (
            <div className={classes.groupFormContainer}>
              <div className={classes.description}>
                Чтобы присвоить товару группу выберите аналог из списка
              </div>
              <div>
                <SearchSelect
                  onBlur={setDirty}
                  fieldName="saleItemId"
                  options={createSaleItemSelectList()}
                  value={data.saleItemId}
                  onChangeHandler={inputHandler}
                  type="text"
                  placeholder={
                    createSaleItemSelectList().length
                      ? 'Начните ввод и выберите товар из списка'
                      : 'Нет товаров в этой категории'
                  }
                  itemValidationResult={getValidationResult('saleItemId')}
                />
                {data.saleItemId && (
                  <Button small text="Очистить" onClick={() => resetField('saleItemId')} />
                )}
              </div>
              {data.saleItemId && getSaleItem(Number(data.saleItemId)) && (
                <>
                  <div className={classes.description}>
                    {getSaleItem(Number(data.saleItemId))?.groupId
                      ? `Товару будет присвоена группа ${
                          getSaleItem(Number(data.saleItemId))?.groupId
                        }`
                      : `Будет создана новая группа для товаров`}
                  </div>
                  {getSaleItem(Number(data.saleItemId))?.groupId &&
                    saleItemList.filter(
                      (item) => item.groupId === getSaleItem(Number(data.saleItemId))?.groupId,
                    ).length > 0 && (
                      <>
                        <div className={classes.description}>Другие товары этой группы</div>
                        <div className={classes.analogsList}>
                          {saleItemList
                            .filter(
                              (item) =>
                                item.groupId === getSaleItem(Number(data.saleItemId))?.groupId,
                            )
                            .map((item) => (
                              <div className={classes.listItem} key={item.id}>
                                {getItemDisplayText(item)}
                              </div>
                            ))}
                        </div>
                      </>
                    )}
                </>
              )}
            </div>
          )}

          {data.groupId && (
            <>
              <div className={classes.description}>{`Группа товара ${el?.groupId}`}</div>
              <Button small text="Отвязать товар" onClick={() => resetField('groupId')} />
              {saleItemList.filter((item) => item.groupId === data.groupId).length > 0 && (
                <>
                  <div className={classes.description}>Другие товары этой группы</div>
                  <div className={classes.analogsList}>
                    {saleItemList
                      .filter((item) => item.groupId === data.groupId)
                      .map((item) => (
                        <div className={classes.listItem} key={item.id}>
                          {getItemDisplayText(item)}
                        </div>
                      ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {(view === SaleItemFormView.FORM || view === SaleItemFormView.ATTRIBUTE) && (
        <div className={classes.groupFormContainer}>
          {view !== SaleItemFormView.ATTRIBUTE && (
            <>
              <div className={classes.toggleContainer}>
                <Toggle
                  id="isAddon"
                  onChange={() => toggleAction('isAddon')}
                  checked={data.isAddon}
                />
                <div>Это добавка (модификатор)</div>
              </div>

              {!data.isAddon && (
                <SearchSelect
                  onBlur={setDirty}
                  fieldName="categoryId"
                  options={createCategorySelectList()}
                  value={data.categoryId}
                  onChangeHandler={inputHandler}
                  type="text"
                  placeholder={
                    createCategorySelectList().length
                      ? 'Начните ввод и выберите категрию из списка'
                      : 'Сначала создайте категории'
                  }
                  itemValidationResult={getValidationResult('categoryId')}
                />
              )}
              <SearchSelect
                onBlur={setDirty}
                fieldName="unit"
                options={createTypeSelectList(Unit, unitTypeToText).filter(
                  (el) => Number(el.value) !== -1,
                )}
                value={data.unit}
                onChangeHandler={inputHandler}
                type="text"
                placeholder={'Начните ввод и выберите единицу измерения'}
                itemValidationResult={getValidationResult('unit')}
              />

              <Input
                type={'text'}
                fieldName={'name'}
                value={data.name}
                placeHolder={'Название товара'}
                onChangeHandler={inputHandler}
                itemValidationResult={getValidationResult('name')}
                onBlur={setDirty}
              />
              <Textarea
                type={'textarea'}
                fieldName={'description'}
                value={data.description}
                placeHolder="Опишите товар подробно"
                rows={5}
                cols={20}
                onChangeHandler={inputHandler}
                itemValidationResult={getValidationResult('description')}
                onBlur={setDirty}
              />

              <Input
                type={'text'}
                label="Минимум в корзине"
                fieldName={'minQuantity'}
                value={data.minQuantity ? data.minQuantity : ''}
                placeHolder={'Минимум в корзине'}
                onChangeHandler={inputHandler}
                itemValidationResult={getValidationResult('minQuantity')}
                onBlur={setDirty}
              />

              <Input
                type={'text'}
                label="Шаг в корзине"
                fieldName={'quantityStep'}
                value={data.quantityStep ? data.quantityStep : ''}
                placeHolder={'Шаг в корзине'}
                onChangeHandler={inputHandler}
                itemValidationResult={getValidationResult('quantityStep')}
                onBlur={setDirty}
              />

              <div className={classes.toggleContainer}>
                <Toggle
                  id="isActive"
                  onChange={() => toggleAction('isActive')}
                  checked={data.isActive}
                />
                <div>Активен</div>
              </div>
              <div className={classes.toggleContainer}>
                <Toggle
                  id="isService"
                  onChange={() => toggleAction('isService')}
                  checked={data.isService}
                />
                <div>Услуга</div>
              </div>

              {!data.isAddon && (
                <div className={classes.toggleContainer}>
                  <Toggle
                    id="useStock"
                    onChange={() => toggleAction('useStock')}
                    checked={data.useStock}
                  />
                  <div>Учитывать остатки</div>
                </div>
              )}
              <Input
                type={'text'}
                fieldName={'barCode'}
                value={data.barCode ? data.barCode : ''}
                placeHolder={'Код'}
                onChangeHandler={inputHandler}
                itemValidationResult={getValidationResult('barCode')}
                onBlur={setDirty}
              />

              <FileUpload
                length={currentCluster?.type === ClusterTypes.DEALER ? 15 : 3}
                min_length={data.isAddon ? 0 : 1}
                extensions={['jpeg', 'png', 'jpg', 'webp', 'mp4', 'webm', 'mov']}
                files={files}
                setFiles={setFiles}
                pairs={pairs}
                setPairs={setPairs}
                maxVideoCount={1}
              />
            </>
          )}

          {selectAttrs(Number(data.categoryId))?.length > 0 && (
            <div
              style={{ display: view !== SaleItemFormView.ATTRIBUTE ? 'none' : 'block' }}
              className={
                view === SaleItemFormView.ATTRIBUTE
                  ? classes.attributesContainer
                  : classes.attributesContainerHidden
              }
            >
              <ItemAttributeForm
                setTopData={setData}
                topData={data}
                setDisabled={setAttrsDisabled}
                categoryAttributes={selectAttrs(Number(data.categoryId))}
                itemAttributes={(el?.attributes as unknown as ItemAttribute[]) || []}
              />
            </div>
          )}
        </div>
      )}

      <Button
        text={
          view === SaleItemFormView.ATTRIBUTE ||
          view === SaleItemFormView.GROUP ||
          view === SaleItemFormView.ADDONS ||
          view === SaleItemFormView.RECOMMENDATIONS ||
          view === SaleItemFormView.KIT ||
          view === SaleItemFormView.AVAILABILITY
            ? 'К сохранению'
            : edit
              ? 'Сохранить'
              : 'Добавить'
        }
        onClick={() => {
          if (
            view === SaleItemFormView.ATTRIBUTE ||
            view === SaleItemFormView.GROUP ||
            view === SaleItemFormView.ADDONS ||
            view === SaleItemFormView.RECOMMENDATIONS ||
            view === SaleItemFormView.KIT ||
            view === SaleItemFormView.AVAILABILITY
          )
            setView(SaleItemFormView.FORM)
          else formAction()
        }}
        disabled={
          view !== SaleItemFormView.GROUP &&
          view !== SaleItemFormView.ADDONS &&
          view !== SaleItemFormView.RECOMMENDATIONS &&
          view !== SaleItemFormView.AVAILABILITY &&
          ((view !== SaleItemFormView.ATTRIBUTE && disabled) ||
            (attrsDisabled && data.attributes.length > 0))
        }
      />
    </div>
  )
}
